import type { ComponentRuntimeDefinition } from '../../catalog/index.ts';
import { z } from 'zod';
import { componentHardMinimum } from '../shared/sizingProfiles.ts';

const choiceConfigSchema = z.object({
  question: z.string().max(500).default(''),
  options: z.array(z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    description: z.string().max(200).optional(),
    icon: z.string().max(200).optional(),
  }).strict()).min(2).max(6).default([{ id: 'a', label: '选项A' }, { id: 'b', label: '选项B' }]),
  allowReselect: z.boolean().default(false),
}).strict();

export const choiceDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.choice@1',
    category: 'control',
    title: '情景决策',
    description: '用户在案例角色中面对有限选项时做出关键决策。',
    capabilities: ['choice-selection', 'interaction-trigger'],
    suitableFor: ['角色决策', '情景判断', '策略选择'],
    inputs: [],
    outputs: [],
    events: [{ type: 'choice.submitted', description: '用户确认选择，payload 含 choiceId 和 choiceLabel' }],
    constraints: ['选项数量 2-6', '默认不可重新选择', '需配合 model.interaction 使用'],
    configSchemaId: 'probfun.choice.config@1',
    configSchema: {
      type: 'object',
      properties: {
        question: { type: 'string', maxLength: 500 },
        options: { type: 'array', minItems: 2, maxItems: 6, items: { type: 'object', required: ['id', 'label'], properties: { id: { type: 'string' }, label: { type: 'string' }, description: { type: 'string' }, icon: { type: 'string' } } } },
        allowReselect: { type: 'boolean', default: false },
      },
      additionalProperties: false,
    },
    defaultConfig: { question: '请做出你的选择', options: [{ id: 'a', label: '选项A' }, { id: 'b', label: '选项B' }], allowReselect: false },
    examples: [{ question: '你是食堂经理，现在排队人数持续增长，你选择？', options: [{ id: 'add', label: '增加一个窗口', description: '降低等待时间但增加成本' }, { id: 'keep', label: '维持现状' }, { id: 'limit', label: '限制进入人数', description: '维持服务效率但可能引起不满' }], allowReselect: false }],
    notes: [
      '点击选项即提交 choice.submitted。',
      '提交后禁用重选（allowReselect 可覆盖）。',
      '配合 action: "model.interaction" 联动 Runtime。',
      '宽或高不足会先藏选项说明再改横/竖排；到达硬最小尺寸后不再缩小、缩放或滚动。',
    ],
    layout: { ...componentHardMinimum('probfun.choice@1'), maxWidth: 600 },
    runtimeRequirements: [],
    metadata: { layoutHints: { roles: ['controls'], preferredSize: 'small', supportsCompact: true, supportsExpanded: true, canStack: true, priority: 55 } },
  },
  configSchema: choiceConfigSchema,
  loader: () => import('./ChoiceBlock.vue').then(m => m.default),
};

const predictionConfigSchema = z.object({
  question: z.string().max(500).default(''),
  predictionType: z.enum(['number', 'option']).default('number'),
  unit: z.string().max(20).optional(),
  min: z.number().finite().optional(),
  max: z.number().finite().optional(),
  options: z.array(z.object({ id: z.string().min(1), label: z.string().min(1) }).strict()).max(10).default([]),
  hint: z.string().max(200).optional(),
}).strict();

export const predictionDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.prediction@1',
    category: 'control',
    title: '预测输入',
    description: '用户在实验运行前输入对结果的预测，实验后对比验证。',
    capabilities: ['prediction-input', 'interaction-trigger'],
    suitableFor: ['结果预测', '假设检验', '猜想验证'],
    inputs: [],
    outputs: [],
    events: [{ type: 'prediction.submitted', description: '用户提交预测，payload 含 predictionType 和 value' }],
    constraints: ['数字预测需指定 min/max', '建议与 feedback.outcome 配对'],
    configSchemaId: 'probfun.prediction.config@1',
    configSchema: {
      type: 'object',
      properties: {
        question: { type: 'string' },
        predictionType: { type: 'string', enum: ['number', 'option'] },
        unit: { type: 'string' },
        min: { type: 'number' },
        max: { type: 'number' },
        options: { type: 'array', maxItems: 10, items: { type: 'object', properties: { id: { type: 'string' }, label: { type: 'string' } } } },
        hint: { type: 'string' },
      },
      additionalProperties: false,
    },
    defaultConfig: { question: '你预测结果会是多少？', predictionType: 'number' },
    examples: [{ question: '你认为平均等待时间会是多少秒？', predictionType: 'number', unit: '秒', min: 0, max: 300, hint: '提示：当前队伍约10人，每人服务约20秒' }],
    notes: ['提交后显示已预测值。', '与 feedback.outcome 配对展示对比。', '宽或高不足时先隐藏提示文字。'],
    layout: { ...componentHardMinimum('probfun.prediction@1'), maxWidth: 500 },
    runtimeRequirements: [],
    metadata: { layoutHints: { roles: ['controls'], preferredSize: 'small', supportsCompact: true, supportsExpanded: true, canStack: true, priority: 55 } },
  },
  configSchema: predictionConfigSchema,
  loader: () => import('./PredictionBlock.vue').then(m => m.default),
};

const perspectiveToggleConfigSchema = z.object({
  defaultMode: z.enum(['role', 'god']).default('role'),
  roleLabel: z.string().max(40).default('我的视角'),
  godLabel: z.string().max(40).default('全局视角'),
}).strict();

export const perspectiveToggleDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.perspective-toggle@1',
    category: 'control',
    title: '视角切换',
    description: '在角色有限视角与上帝全知视角之间切换，控制页面 Block 可见性。',
    capabilities: ['perspective-toggle', 'interaction-trigger'],
    suitableFor: ['角色视角', '全局视角', '信息分层'],
    inputs: [],
    outputs: [],
    events: [{ type: 'perspective.changed', description: '视角切换，payload 含 mode' }],
    constraints: ['视角变化后由其他 Block 的 interaction binding 响应'],
    configSchemaId: 'probfun.perspective-toggle.config@1',
    configSchema: {
      type: 'object',
      properties: {
        defaultMode: { type: 'string', enum: ['role', 'god'], default: 'role' },
        roleLabel: { type: 'string', maxLength: 40, default: '我的视角' },
        godLabel: { type: 'string', maxLength: 40, default: '全局视角' },
      },
      additionalProperties: false,
    },
    defaultConfig: { defaultMode: 'role', roleLabel: '我的视角', godLabel: '全局视角' },
    examples: [{ defaultMode: 'role', roleLabel: '学生视角', godLabel: '系统视角' }],
    notes: ['方案A信号灯模式：只发事件，不控制数据。', '配合 presentation.show/hide 使用。'],
    layout: { ...componentHardMinimum('probfun.perspective-toggle@1'), maxWidth: 200 },
    runtimeRequirements: [],
    metadata: { layoutHints: { roles: ['controls'], preferredSize: 'small', supportsCompact: true, supportsExpanded: false, canStack: true, priority: 40 } },
  },
  configSchema: perspectiveToggleConfigSchema,
  loader: () => import('./PerspectiveToggleBlock.vue').then(m => m.default),
};
